import { useMutation } from '@tanstack/react-query';

import {
  type CardDispute,
  type CloseCardDisputeRequest,
  type CreateCardDisputeRequest,
  type RespondCardDisputeRequest,
  cardsCloseCardDispute,
  cardsCreateCardDispute,
  cardsRespondCardDispute,
  cardsSubmitCardDispute,
} from '@column/api';

import { useCardDisputeMutationWrapper } from './useCardDisputeMutationWrapper';

export interface CreateCardDisputeVariables {
  request: CreateCardDisputeRequest;
  transactionId: string;
}

export const useCreateCardDispute = () => {
  const wrapMutation = useCardDisputeMutationWrapper();

  return useMutation(
    wrapMutation(
      {
        mutationFn: async ({ request }: CreateCardDisputeVariables): Promise<CardDispute> => {
          const { data } = await cardsCreateCardDispute({ body: request, throwOnError: true });
          return data;
        },
      },
      (_data, variables) => ({ transactionId: variables.transactionId })
    )
  );
};

export interface SubmitCardDisputeVariables {
  disputeId: string;
  transactionId: string;
}

export const useSubmitCardDispute = () => {
  const wrapMutation = useCardDisputeMutationWrapper();

  return useMutation(
    wrapMutation(
      {
        mutationFn: async ({ disputeId }: SubmitCardDisputeVariables): Promise<CardDispute> => {
          const { data } = await cardsSubmitCardDispute({
            path: { card_dispute_id: disputeId },
            throwOnError: true,
          });
          return data;
        },
      },
      (_data, variables) => ({ disputeId: variables.disputeId, transactionId: variables.transactionId })
    )
  );
};

export interface RespondCardDisputeVariables {
  disputeId: string;
  transactionId: string;
  request: RespondCardDisputeRequest;
}

export const useRespondCardDispute = () => {
  const wrapMutation = useCardDisputeMutationWrapper();

  return useMutation(
    wrapMutation(
      {
        mutationFn: async ({ disputeId, request }: RespondCardDisputeVariables): Promise<CardDispute> => {
          const { data } = await cardsRespondCardDispute({
            path: { card_dispute_id: disputeId },
            body: request,
            throwOnError: true,
          });
          return data;
        },
      },
      (_data, variables) => ({ disputeId: variables.disputeId, transactionId: variables.transactionId })
    )
  );
};

export interface CloseCardDisputeVariables {
  disputeId: string;
  transactionId: string;
  request: CloseCardDisputeRequest;
}

export const useCloseCardDispute = () => {
  const wrapMutation = useCardDisputeMutationWrapper();

  return useMutation(
    wrapMutation(
      {
        mutationFn: async ({ disputeId, request }: CloseCardDisputeVariables): Promise<CardDispute> => {
          const { data } = await cardsCloseCardDispute({
            path: { card_dispute_id: disputeId },
            body: request,
            throwOnError: true,
          });
          return data;
        },
      },
      (_data, variables) => ({ disputeId: variables.disputeId, transactionId: variables.transactionId })
    )
  );
};

/** The API error `type` code from a thrown generated-client error, when present. */
export const apiErrorType = (error: unknown): string | undefined => {
  if (typeof error === 'object' && error && 'type' in error && typeof (error as { type: unknown }).type === 'string') {
    return (error as { type: string }).type;
  }
  return undefined;
};

/** The API error message from a thrown generated-client error, when present. */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  if (
    typeof error === 'object' &&
    error &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
};

/**
 * A named entry from the thrown error's `details` map (e.g. `card_dispute_id` on the
 * already-disputed guard). The generated error type only documents code/message/type, but the
 * backend serializes `details` on every APIError.
 */
export const apiErrorDetail = (error: unknown, key: string): string | undefined => {
  if (typeof error !== 'object' || !error || !('details' in error)) {
    return undefined;
  }
  const details = (error as { details: unknown }).details;
  if (typeof details !== 'object' || details === null) {
    return undefined;
  }
  const value = (details as Record<string, unknown>)[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
};
